import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Users, Briefcase, DollarSign, TrendingUp, AlertTriangle, Clock, CheckCircle, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate } from 'react-router-dom';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LogoLoader from '@/components/LogoLoader';
import { formatTimestampForFilename } from '@/utils/date';

const Dashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poSummary, setPoSummary] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [eraseLoading, setEraseLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [showLogoArrival, setShowLogoArrival] = useState(() => Boolean(location.state?.fromLogin));

  const COLORS = ['#3D5B49', '#A0B9A5', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3'];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location.state?.fromLogin) {
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!showLogoArrival) return;
    const timer = setTimeout(() => setShowLogoArrival(false), 1200);
    return () => clearTimeout(timer);
  }, [showLogoArrival]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, poSummaryData, analyticsData] = await Promise.all([
        api.get('/dashboard/stats', token),
        api.get('/po/summary', token),
        api.get('/accounting/analytics/summary', token).catch(() => null)
      ]);
      const mergedStats = {
        ...statsData,
        analytics: analyticsData || statsData.analytics || null
      };
      setStats(mergedStats);
      setPoSummary(poSummaryData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const blobData = await api.get('/admin/backup', token, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blobData);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utis-backup-${formatTimestampForFilename()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Backup failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleEraseWithBackup = async () => {
    if (!window.confirm('This will erase ALL operational data. A backup will be downloaded first. Continue?')) return;
    setEraseLoading(true);
    try {
      const blobData = await api.post(
        '/admin/erase-with-backup',
        { confirm: 'WIPE-UTIS-DATA' },
        token,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(blobData);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utis-backup-before-wipe-${formatTimestampForFilename()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data erased after backup');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Erase failed');
    } finally {
      setEraseLoading(false);
    }
  };

  const openRestorePicker = () => {
    if (!restoreLoading) {
      fileInputRef.current?.click();
    }
  };

  const handleRestore = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      toast.error('Please choose a .zip backup');
      evt.target.value = '';
      return;
    }

    const form = new FormData();
    form.append('file', file);
    form.append('replace', 'true');
    setRestoreLoading(true);

    try {
      await api.post('/admin/restore', form, token, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Restore completed');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Restore failed');
    } finally {
      setRestoreLoading(false);
      evt.target.value = '';
    }
  };

  if (loading) {
    return <LogoLoader message="Loading dashboard insights..." />;
  }

  const formatCurrency = (value) => `$${Number(value ?? 0).toLocaleString()}`;

  const activePoDetails = poSummary?.details
    ? [...poSummary.details]
        .filter((po) => po.status === 'Active')
        .sort((a, b) => Number(b.value_pct ?? 0) - Number(a.value_pct ?? 0))
    : [];

  return (
    <div className="relative space-y-6" data-testid="dashboard-page">
      {showLogoArrival && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-start justify-end pr-8 pt-6">
          <img
            src="/assets/utis-logo.png"
            alt="UTIS Logo"
            className="h-16 w-auto animate-dashboard-logo-arrival"
          />
        </div>
      )}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your accounting operations</p>
      </div>

      {/* Admin tools */}
      {user?.role === 'Admin' && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            onClick={handleBackup}
            disabled={backupLoading}
            variant="outline"
            className="rounded-2xl"
          >
            {backupLoading ? 'Downloading...' : 'Backup Data'}
          </Button>
          <Button
            onClick={handleEraseWithBackup}
            disabled={eraseLoading}
            className="rounded-2xl bg-red-600 text-white hover:bg-red-700"
          >
            {eraseLoading ? 'Erasing…' : 'Erase Data (with Backup)'}
          </Button>
          <Button
            onClick={openRestorePicker}
            disabled={restoreLoading}
            variant="outline"
            className="rounded-2xl"
          >
            {restoreLoading ? 'Restoring…' : 'Restore From Backup'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={handleRestore}
          />
        </div>
      )}

      {/* Stats Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="border-blue-100 hover:shadow-xl transition-all cursor-pointer hover:scale-105"
          onClick={() => navigate('/clients')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.total_clients || 0}</p>
                <p className="text-xs text-blue-600 mt-2 font-medium">View all clients →</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-green-100 hover:shadow-xl transition-all cursor-pointer hover:scale-105"
          onClick={() => navigate('/projects')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Projects</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.active_projects || 0}</p>
                <p className="text-xs text-green-600 mt-2 font-medium">View projects →</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-utis-sage/20 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-purple-100 hover:shadow-xl transition-all cursor-pointer hover:scale-105"
          onClick={() => navigate('/invoices')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Billed</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">${(stats?.total_billed || 0).toLocaleString()}</p>
                <p className="text-xs text-purple-600 mt-2 font-medium">View invoices →</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-orange-100 hover:shadow-xl transition-all cursor-pointer hover:scale-105"
          onClick={() => navigate('/time-entries')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.pending_approvals || 0}</p>
                <p className="text-xs text-orange-600 mt-2 font-medium">Review entries →</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PO Alerts */}
      {stats?.po_alerts && stats.po_alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center text-red-900">
              <AlertTriangle className="w-5 h-5 mr-2" />
              PO Consumption Alerts
            </CardTitle>
            <CardDescription>Purchase Orders reaching 80% threshold</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.po_alerts.map((alert) => (
                <div
                  key={alert.po_id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate('/projects')}
                >
                  <div>
                    <p className="font-bold text-red-900">{alert.po_number}</p>
                    <p className="text-sm text-gray-600">
                      Value consumption: {Math.round(Number(alert.value_pct ?? 0))}%
                    </p>
                  </div>
                  <Badge className="bg-red-600 text-white">
                    {Math.round(Number(alert.value_pct ?? 0))}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Expense Trend */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-utis-forest" />
              Revenue & Expense Trend
            </CardTitle>
            <CardDescription>Last 6 months performance</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats?.revenue_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#666" style={{ fontSize: '12px' }} />
                <YAxis stroke="#666" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}
                  formatter={(value) => `$${value.toLocaleString()}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#3D5B49" 
                  strokeWidth={3}
                  name="Revenue"
                  dot={{ fill: '#3D5B49', r: 5 }}
                  activeDot={{ r: 8, onClick: () => navigate('/invoices') }}
                />
                <Line 
                  type="monotone" 
                  dataKey="expenses" 
                  stroke="#FF6B6B" 
                  strokeWidth={3}
                  name="Expenses"
                  dot={{ fill: '#FF6B6B', r: 5 }}
                  activeDot={{ r: 8, onClick: () => navigate('/expenses') }}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/reports')}
                className="text-utis-forest border-utis-forest hover:bg-utis-sage/20"
              >
                View Detailed Reports →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Expense Breakdown Pie Chart */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-utis-forest" />
              Expense Distribution
            </CardTitle>
            <CardDescription>Breakdown by expense type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats?.expense_breakdown || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={() => navigate('/expenses')}
                  style={{ cursor: 'pointer' }}
                >
                  {(stats?.expense_breakdown || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {(stats?.expense_breakdown || []).map((item, index) => (
                <div key={item.name} className="flex items-center space-x-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span className="text-gray-700">{item.name}: ${item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/expenses')}
                className="text-utis-forest border-utis-forest hover:bg-utis-sage/20"
              >
                Manage Expenses →
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {poSummary?.details && poSummary.details.length > 0 && (
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl shadow p-4">
          <h3 className="font-semibold text-[#3D5B49] mb-3">Detailed PO Consumption</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b">
                  <th className="text-left py-2 px-2">PO Number</th>
                  <th className="text-left py-2 px-2">Client</th>
                  <th className="text-right py-2 px-2">Total Value ($)</th>
                  <th className="text-right py-2 px-2">Consumed ($)</th>
                  <th className="text-right py-2 px-2">Remaining ($)</th>
                  <th className="text-center py-2 px-2">Consumption %</th>
                  <th className="text-center py-2 px-2">Status</th>
                </tr>
              </thead>

              <tbody>
                {activePoDetails.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-gray-500">
                      No active purchase orders to display.
                    </td>
                  </tr>
                ) : (
                  activePoDetails.map((po) => {
                    const pct = Number(po.value_pct ?? 0);
                    const threshold = Number(po.alert_threshold ?? 80);
                    const badgeClass =
                      pct >= 100
                        ? 'bg-red-600'
                        : pct >= threshold
                        ? 'bg-orange-500'
                        : 'bg-green-600';
                    const pctWidth = Math.min(pct, 100);

                    return (
                      <tr key={po.po_id} className="border-b hover:bg-gray-50 transition">
                        <td className="py-2 px-2 font-medium text-gray-800">{po.po_number || '—'}</td>
                        <td className="py-2 px-2 text-gray-600">
                          {po.client_name || po.client_id || 'Unknown'}
                        </td>
                        <td className="py-2 px-2 text-right">{formatCurrency(po.value_cap)}</td>
                        <td className="py-2 px-2 text-right text-[#e57373]">
                          {formatCurrency(po.consumed_value)}
                          <div className="text-xs text-gray-400">
                            includes ${Number(po.unbilled_value ?? 0).toLocaleString()} unbilled
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right text-[#3D5B49]">{formatCurrency(po.remaining_value)}</td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-24 h-2 rounded-full bg-gray-200">
                              <div className={`${badgeClass} h-2 rounded-full`} style={{ width: `${pctWidth}%` }} />
                            </div>
                            <span className="text-xs text-gray-600 mt-1">{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className={`px-2 py-1 text-xs rounded-lg text-white ${badgeClass}`}>
                            {pct >= 100 ? 'Exceeded' : pct >= threshold ? 'Warning' : 'Healthy'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Status */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-utis-forest" />
              Consultant Payment Status
            </CardTitle>
            <CardDescription>Outstanding vs paid consultant payments</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.payment_status || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}
                  formatter={(value) => `$${value.toLocaleString()}`}
                />
                <Bar 
                  dataKey="value" 
                  fill="#3D5B49"
                  onClick={() => navigate('/consultant-payments')}
                  style={{ cursor: 'pointer' }}
                >
                  {(stats?.payment_status || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.name === 'Unpaid' ? '#FF6B6B' : '#4ECDC4'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {(stats?.payment_status || []).map((item) => (
                <div key={item.name} className="p-3 rounded-lg" style={{ 
                  backgroundColor: item.name === 'Unpaid' ? '#FEE2E2' : '#D1FAE5' 
                }}>
                  <p className="text-sm font-medium" style={{ 
                    color: item.name === 'Unpaid' ? '#991B1B' : '#065F46' 
                  }}>
                    {item.name}
                  </p>
                  <p className="text-2xl font-bold mt-1" style={{ 
                    color: item.name === 'Unpaid' ? '#991B1B' : '#065F46' 
                  }}>
                    ${item.value.toLocaleString()}
                  </p>
                  <p className="text-xs mt-1" style={{ 
                    color: item.name === 'Unpaid' ? '#991B1B' : '#065F46' 
                  }}>
                    {item.count} entries
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/consultant-payments')}
                className="text-utis-forest border-utis-forest hover:bg-utis-sage/20"
              >
                Manage Payments →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2 text-utis-forest" />
              Financial Summary
            </CardTitle>
            <CardDescription>Overall financial health</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div 
                className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/invoices')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900">Total Billed</p>
                    <p className="text-3xl font-bold text-green-900 mt-1">
                      ${(stats?.total_billed || 0).toLocaleString()}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-700" />
                </div>
              </div>

              <div 
                className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/receipts')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">Total Collected</p>
                    <p className="text-3xl font-bold text-blue-900 mt-1">
                      ${(stats?.total_collected || 0).toLocaleString()}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-blue-700" />
                </div>
              </div>

              <div
                className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/invoices')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-900">Outstanding</p>
                    <p className="text-3xl font-bold text-orange-900 mt-1">
                      ${(stats?.outstanding || 0).toLocaleString()}
                    </p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-700" />
                </div>
              </div>

              {stats?.analytics && (
                <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
                  <div className="p-4 border rounded-lg bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Overdue</p>
                        <p className="text-2xl font-semibold text-red-700 mt-1">
                          ${(Number(stats.analytics.total_overdue || 0)).toLocaleString()}
                        </p>
                      </div>
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Snapshot generated {stats.analytics.generated_at ? new Date(stats.analytics.generated_at).toLocaleDateString() : 'recently'}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Average Days to Pay</p>
                        <p className="text-2xl font-semibold text-utis-forest mt-1">
                          {stats.analytics.average_days_to_pay != null
                            ? Number(stats.analytics.average_days_to_pay).toFixed(1)
                            : '—'}
                        </p>
                      </div>
                      <Clock className="w-6 h-6 text-utis-forest" />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Payments received this period: ${(
                        Number(stats.analytics.payments_received_this_period || 0)
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              <div className="pt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Collection Rate</span>
                  <span className="font-bold text-utis-forest">
                    {stats?.total_billed ? ((stats.total_collected / stats.total_billed) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-utis-forest h-2 rounded-full transition-all"
                    style={{ width: `${stats?.total_billed ? ((stats.total_collected / stats.total_billed) * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
